import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { usePermission, usePermissionScopes } from '@/features/auth/permissions'
import { createDocument, deleteDocument, fetchDocument, fetchDocuments, saveDocument } from './api'

/**
 * Documents & SOPs. LIVE.
 *
 * ── Four permissions, and they do different things ───────────────────────
 * `documents.manage` (account or plant) files, edits and deletes documents
 * and attaches them anywhere. `documents.attach_assets` attaches to plants,
 * lines, machines and sensors; `documents.attach_classes` to asset classes;
 * `documents.view` reads. Any of the four reads the register — each one sees
 * a different slice of it, which the server decides per request.
 *
 * ── The OWNER plant is what a manage grant has to cover ───────────────────
 * Editing anything but the attachments, replacing the file and deleting all
 * need a manage grant over the document's owner plant (`DocumentGrants
 * .can_edit`). An account-level grant covers every plant AND the account-wide
 * (null) owner; a plant grant covers only its own plants. So filing an
 * account-wide document needs an account-level grant, and `canOwnAccountWide`
 * says whether to offer it.
 *
 * Someone with only an attach key can still open the editor — for the
 * attachments, and nothing else. `canEditDocument` is the line between the two.
 *
 * Not authorisation: the server checks the SCOPE of every attach grant per
 * target, which this does not mirror. Its refusal arrives keyed on the target
 * list and the form shows it.
 *
 * `is_root` bypasses, as it does server-side.
 */
export function useDocumentAccess() {
  const manage = usePermissionScopes('documents.manage')
  const attachAssets = usePermission('documents.attach_assets')
  const attachClasses = usePermission('documents.attach_classes')
  const view = usePermission('documents.view')

  const canAttachAssets = manage.granted || attachAssets.granted
  const canAttachClasses = manage.granted || attachClasses.granted

  /** `DocumentGrants.can_edit` — a manage grant covering the owner plant. */
  const canEditDocument = (doc) =>
    manage.accountLevel || Boolean(doc?.ownerPlantId && manage.plantIds.has(doc.ownerPlantId))

  return {
    isPending: manage.isPending || attachAssets.isPending || attachClasses.isPending || view.isPending,
    canRead: manage.granted || canAttachAssets || canAttachClasses || view.granted,
    canCreate: manage.granted,
    canOwnAccountWide: manage.accountLevel,
    /** `verify_owner_plant` — whether a manage grant covers filing a document owned by this plant. */
    canOwnPlant: (plantId) => manage.accountLevel || manage.plantIds.has(plantId),
    canAttachAssets,
    canAttachClasses,
    canEditDocument,
    /** Whether the editor opens at all — for the whole record, or for its attachments. */
    canEditAttachments: (doc) => canEditDocument(doc) || canAttachAssets || canAttachClasses,
    /** `delete_document` needs `documents.manage` AND `can_edit`. */
    canDelete: (doc) => manage.granted && canEditDocument(doc),
  }
}

/**
 * One page of the register.
 *
 * `placeholderData` keeps the rows on screen while a filter or a search lands.
 * The search is the server's and is debounced by the page, so every accepted
 * keystroke is a request; blanking a table between them makes typing feel like
 * the screen is failing.
 *
 * Thirty seconds is the client default and is left alone. A document register
 * is not a live surface, but it is one several people write to, and a row
 * somebody else filed this morning should not need a reload.
 */
export const useDocuments = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.documents(params),
    queryFn: ({ signal }) => fetchDocuments(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  })

/**
 * One document, with its attachments and revisions.
 *
 * `staleTime: 0, gcTime: 0`, for the reason `useSensor` and `useWorkOrder` set
 * them, and one more that is specific to this resource.
 *
 * The first reason: `PUT` is a full replace and the edit form sends back
 * everything it was given, so a cached copy someone else has since changed
 * would be silently restored.
 *
 * The second: every revision carries `document_view_url`, a PRESIGNED address
 * with a short life, resolved per request. Caching this response caches those
 * URLs past their expiry, and the failure is a preview that 403s on a document
 * the reader can plainly see listed.
 */
export const useDocument = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.document(id),
    queryFn: ({ signal }) => fetchDocument(id, signal),
    enabled: enabled && Boolean(id),
    staleTime: 0,
    gcTime: 0,
  })

/**
 * Every write sweeps the whole `['documents']` root.
 *
 * Not a cache patch: the server assigns the revision number, resolves a fresh
 * view URL, and re-derives `counts.document_type` over the scoped set — none of
 * which this client can compute. A create also has to appear under whatever
 * type chip and attachment filter the reader happens to have on.
 *
 * Not retried, either. Every way these fail is an answer — a target the account
 * does not own, a file that is not a confirmed upload, content already attached
 * somewhere — and asking again gets the same one. The duplicate case is worse
 * than useless to retry: the server destroys the uploaded object as it refuses,
 * so a second attempt with the same file id fails for a different reason.
 */
function useDocumentMutation(mutationFn) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  })
}

export const useCreateDocument = () => useDocumentMutation(createDocument)
export const useSaveDocument = () => useDocumentMutation(saveDocument)
export const useDeleteDocument = () => useDocumentMutation(deleteDocument)
